import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@saiens/shared/hooks';
import { useAuthStore } from '@saiens/shared/stores';
import { Toast } from '@/components/common/Toast';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MainLayout } from '@/components/layout/MainLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { BindingPage } from '@/pages/auth/BindingPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CrmListPage } from '@/pages/crm/CrmListPage';
import { CrmDetailPage } from '@/pages/crm/CrmDetailPage';
import { CrmCreatePage } from '@/pages/crm/CrmCreatePage';
import { SaleListPage } from '@/pages/sale/SaleListPage';
import { SaleDetailPage } from '@/pages/sale/SaleDetailPage';
import { RecentPaymentsPage } from '@/pages/sale/RecentPaymentsPage';
import { ConstructionListPage } from '@/pages/construction/ConstructionListPage';
import { ConstructionDetailPage } from '@/pages/construction/ConstructionDetailPage';
import { ConstructionCreatePage } from '@/pages/construction/ConstructionCreatePage';
import { QuoteViewPage } from '@/pages/quote/QuoteViewPage';
import { BizcardImportPage } from '@/pages/bizcard/BizcardImportPage';
import { ShowroomImportPage } from '@/pages/showroom/ShowroomImportPage';
import { AcceptanceListPage } from '@/pages/acceptance/AcceptanceListPage';
import { AcceptanceFormPage } from '@/pages/acceptance/AcceptanceFormPage';
import { AcceptanceSignPage } from '@/pages/acceptance/AcceptanceSignPage';
import { AcceptanceDetailPage } from '@/pages/acceptance/AcceptanceDetailPage';
import { AcceptanceManagePage } from '@/pages/acceptance/AcceptanceManagePage';
import { PaymentStatusPage } from '@/pages/acceptance/PaymentStatusPage';
import { HrCategoryPage } from '@/pages/category/HrCategoryPage';
import { BusinessCategoryPage } from '@/pages/category/BusinessCategoryPage';
import { ProjectCategoryPage } from '@/pages/category/ProjectCategoryPage';
import { AttendancePage } from '@/pages/attendance/AttendancePage';
import { LeaveHubPage } from '@/pages/leave/LeaveHubPage';
import { LeaveListPage } from '@/pages/leave/LeaveListPage';
import { LeaveApplyPage } from '@/pages/leave/LeaveApplyPage';
import { LeaveDetailPage } from '@/pages/leave/LeaveDetailPage';
import { OvertimeListPage } from '@/pages/overtime/OvertimeListPage';
import { OvertimeApplyPage } from '@/pages/overtime/OvertimeApplyPage';
import { OvertimeDetailPage } from '@/pages/overtime/OvertimeDetailPage';
import { ApprovalHubPage } from '@/pages/approval/ApprovalHubPage';
import { PendingApprovalsPage } from '@/pages/approval/PendingApprovalsPage';
import { ApprovalHistoryPage } from '@/pages/approval/ApprovalHistoryPage';
import { ApprovalDetailPage } from '@/pages/approval/ApprovalDetailPage';
import { AllLeaveRecordsPage } from '@/pages/approval/AllLeaveRecordsPage';
import { AllOvertimeRecordsPage } from '@/pages/approval/AllOvertimeRecordsPage';
import { AllAttendanceRecordsPage } from '@/pages/approval/AllAttendanceRecordsPage';
import { PaymentDelayApprovalsPage } from '@/pages/approval/PaymentDelayApprovalsPage';
import { AccountingHubPage } from '@/pages/accounting/AccountingHubPage';
import { CompletedListPage } from '@/pages/accounting/CompletedListPage';
import { AcceptedListPage } from '@/pages/accounting/AcceptedListPage';
import { InvoicedListPage } from '@/pages/accounting/InvoicedListPage';
import { PaidListPage } from '@/pages/accounting/PaidListPage';
import { AcceptanceOverviewPage } from '@/pages/accounting/AcceptanceOverviewPage';
import { DraftListPage } from '@/pages/accounting/DraftListPage';
import { AllocationListPage } from '@/pages/allocation/AllocationListPage';
import { TicketListPage } from '@/pages/ticket/TicketListPage';
import { TicketFormPage } from '@/pages/ticket/TicketFormPage';
import { TicketDetailPage } from '@/pages/ticket/TicketDetailPage';
import { StockListPage } from '@/pages/stock/StockListPage';
import { StockFormPage } from '@/pages/stock/StockFormPage';
import { StockDetailPage } from '@/pages/stock/StockDetailPage';

const LIFF_ID = import.meta.env.VITE_LIFF_ID || '';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { status, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  if (status === 'not_bound') {
    return <Navigate to="/bind" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { initialize } = useAuth();

  useEffect(() => {
    if (LIFF_ID) {
      initialize(LIFF_ID);
    } else {
      // LIFF_ID not configured - for development, skip auth
      console.warn('VITE_LIFF_ID not configured, skipping LIFF initialization');
      // Set to unauthenticated to show login page
      useAuthStore.getState().setStatus('unauthenticated');
    }
  }, [initialize]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/bind" element={<BindingPage />} />
        <Route path="/quote/view/:token" element={<QuoteViewPage />} />
        <Route path="/acceptance/sign/:token" element={<AcceptanceSignPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="hr" element={<HrCategoryPage />} />
          <Route path="business" element={<BusinessCategoryPage />} />
          <Route path="project" element={<ProjectCategoryPage />} />
          <Route path="crm" element={<CrmListPage />} />
          {/* crm/new 必須排在 crm/:id 之前，否則會被當成 id="new" */}
          <Route path="crm/new" element={<CrmCreatePage />} />
          <Route path="crm/:id" element={<CrmDetailPage />} />
          <Route path="sale" element={<SaleListPage />} />
          <Route path="sale/recent-payments" element={<RecentPaymentsPage />} />
          <Route path="sale/:id" element={<SaleDetailPage />} />
          <Route path="construction" element={<ConstructionListPage />} />
          <Route path="construction/create" element={<ConstructionCreatePage />} />
          <Route path="construction/:id" element={<ConstructionDetailPage />} />
          <Route path="bizcard" element={<BizcardImportPage />} />
          <Route path="showroom" element={<ShowroomImportPage />} />
          <Route path="acceptance" element={<AcceptanceListPage />} />
          <Route path="acceptance/manage" element={<AcceptanceManagePage />} />
          <Route path="acceptance/payment-status" element={<PaymentStatusPage />} />
          <Route path="acceptance/create/:orderId" element={<AcceptanceFormPage />} />
          <Route path="acceptance/:id" element={<AcceptanceDetailPage />} />
          <Route path="acceptance/:id/edit" element={<AcceptanceFormPage />} />
          <Route path="attendance" element={<AttendancePage />} />
          <Route path="leave-hub" element={<LeaveHubPage />} />
          <Route path="leave" element={<LeaveListPage />} />
          <Route path="leave/apply" element={<LeaveApplyPage />} />
          <Route path="leave/:id" element={<LeaveDetailPage />} />
          <Route path="overtime" element={<OvertimeListPage />} />
          <Route path="overtime/apply" element={<OvertimeApplyPage />} />
          <Route path="overtime/:id" element={<OvertimeDetailPage />} />
          <Route path="approval" element={<ApprovalHubPage />} />
          <Route path="approval/pending" element={<PendingApprovalsPage />} />
          <Route path="approval/history" element={<ApprovalHistoryPage />} />
          <Route path="approval/:id" element={<ApprovalDetailPage />} />
          <Route path="approval/payment-delays" element={<PaymentDelayApprovalsPage />} />
          <Route path="approval/leave-all" element={<AllLeaveRecordsPage />} />
          <Route path="approval/overtime-all" element={<AllOvertimeRecordsPage />} />
          <Route path="approval/attendance-all" element={<AllAttendanceRecordsPage />} />
          <Route path="accounting" element={<AccountingHubPage />} />
          <Route path="accounting/completed" element={<CompletedListPage />} />
          <Route path="accounting/accepted" element={<AcceptedListPage />} />
          <Route path="accounting/invoiced" element={<InvoicedListPage />} />
          <Route path="accounting/paid" element={<PaidListPage />} />
          <Route path="accounting/overview" element={<AcceptanceOverviewPage />} />
          <Route path="accounting/overview/draft" element={<DraftListPage />} />
          <Route path="allocation" element={<AllocationListPage />} />
          <Route path="ticket" element={<TicketListPage />} />
          <Route path="ticket/create" element={<TicketFormPage />} />
          <Route path="ticket/:id" element={<TicketDetailPage />} />
          <Route path="ticket/:id/edit" element={<TicketFormPage />} />
          <Route path="stock" element={<StockListPage />} />
          <Route path="stock/create" element={<StockFormPage />} />
          <Route path="stock/:id" element={<StockDetailPage />} />
          <Route path="stock/:id/edit" element={<StockFormPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toast />
    </BrowserRouter>
  );
}

export default App;
